export default {
    title: 'Help',
    sideNavTitle: 'Contents',
    sectionJumpPlaceholder: 'Jump to section',
    sections: {
        intro: 'Welcome',
        navigation: 'Getting around',
        dashboard: 'Dashboard',
        events: 'Events',
        ticketsets: 'Ticket sets',
        tickets: 'Tickets',
        devices: 'Devices',
        statistics: 'Statistics',
        users: 'Users',
        permissions: 'Permissions (IUR)',
        import: 'CSV ticket import',
        shortcuts: 'Keyboard shortcuts',
        troubleshooting: 'What to do when',
        contact: 'Contact and bug reports'
    },
    intro: {
        title: 'Welcome',
        intro:
            'Bzuco Inspector is the admin console for managing tickets and validating them at event entrances. This help describes how to use each section. The app runs in parallel with the legacy admin on /admin/ — you can keep both open and migrate gradually.',
        points: [
            'You log in with the same credentials as the legacy admin.',
            'Data is shared — tickets imported here also appear in the old admin.',
            'Ticket sets and events are pushed in by Bzuco POS sync; they are not created by hand in this admin.'
        ]
    },
    navigation: {
        title: 'Getting around',
        intro:
            'The app has three main regions: a top bar with avatar and breadcrumbs, a left sidebar listing sections, and the main content area. Sections are grouped by purpose: Overview, Tickets, and Settings.',
        points: [
            'Top bar: click the avatar (initials) to open the menu — sign out, switch instance (if you have access to multiple).',
            'Breadcrumbs show your current position; click any segment to navigate up.',
            'Sidebar: Overview / Tickets / Settings groups. Click an item to switch to that section.',
            'Global shortcut: press "/" anywhere to open the filter bar (where one exists) and focus its search input.',
            'Some sections require a permission (Manage events, Statistics, …) — without it the section is hidden from the menu.'
        ]
    },
    dashboard: {
        title: 'Dashboard',
        intro:
            'The home page after login. It is an operational overview — what is happening right now, where the problems are — not an analytics tool. Use Statistics for analytics.',
        points: [
            'KPI cards: Validations in 24 h (with sparkline), Tickets (used/total), Active devices, Events ±7 days. Visibility depends on your permissions.',
            'Active events (max 3): ongoing today first, then upcoming, then recently ended.',
            'Recent scans: 20 latest successful validations from TicketUsageLog.',
            'Alerts: three types — DEVICE_OFFLINE (device silent > 2 h during an event), EVENT_NO_SCANS (event starts in < 24 h with 0 validations), BROKEN_TICKETSET (ticket set without prices — cannot be scanned).',
            'Quick actions: import tickets, create event, create device.',
            'Auto-refresh every 60 s plus a manual button in the top right. Polling pauses automatically when the tab is hidden, saving battery.',
            'Superusers additionally see a table of all instances; clicking a row switches the active instance.'
        ]
    },
    events: {
        title: 'Events',
        intro:
            'List of events connected to Bzuco POS. Clicking a row opens an expand panel with editing. Synced fields (name, dates) are editable — IDs and Bzuco IDs are read-only.',
        points: [
            'Filter: search (name) + status (upcoming / live / ended). Status is computed client-side from startAt and endAt.',
            'Bulk-select + bulk-delete: the backend has no deleteMulti, so deletion runs in a loop via Promise.allSettled — individual failures will not block the rest.',
            'Events originate in Bzuco POS and arrive via sync. You can edit them here, but you cannot create a brand-new event without POS.'
        ]
    },
    ticketsets: {
        title: 'Ticket sets',
        intro:
            'Ticket sets group tickets of the same type (e.g. "VIP Ticket"). Sets are created in Bzuco POS and arrive through sync. In this section you can only edit the name and the "validate" flag — full ticket sets cannot be created here.',
        points: [
            'The expand panel has 3 sections: Edit (name, customName, validate flag), Metadata (read-only dates, ID, Bzuco ID), Price levels (read-only sub-table).',
            'Click the "Tickets" column → goes to /tickets?ticketset=<id> — shows only tickets of that specific set.',
            'If a set is missing, check Bzuco POS sync — the set must arrive first, otherwise tickets cannot be assigned to it.'
        ]
    },
    tickets: {
        title: 'Tickets',
        intro:
            'The main data section — typically thousands of records. Uses cursor pagination with 50 / 100 / 200 tickets per page and shows "1–N of total". Clicking a row opens an expand panel with editing.',
        points: [
            'Filters: search (code / name / email / phone), event (with search inside the select), ticket set, usage (3-state: Yes / No / both).',
            'Filters are synced to the URL query string (debounced 300 ms) — the link can be shared and preserves the filter state.',
            'Bulk-select + bulk-delete. CSV export of the current page.',
            'CSV import: a button leads to /tickets/import — a 4-step wizard (upload → preview → mapping → confirmation). See "CSV ticket import" below.',
            'The global "/" shortcut opens the filter bar and focuses the search input.'
        ]
    },
    devices: {
        title: 'Devices',
        intro:
            'Devices are the mobile scanners (PDAs, phones with the scanner app) that validate tickets at the entrance. Full CRUD — you can create, edit, and delete devices.',
        points: [
            'Create/Edit dialog has name, username, password fields. The username is a unique login used by the scanner app.',
            'When editing, leave the password empty if you do not want to change it — empty values are not saved.',
            'Filter: search matches both name and username.',
            'Bulk-select + bulk-delete works the same way as for events (Promise.allSettled).'
        ]
    },
    statistics: {
        title: 'Statistics',
        intro:
            'Analytics tool for events, ticket sets, and devices. Requires the "Statistics" permission — without it the section is hidden from the menu.',
        points: [
            'Filter card (collapsible): events multi-select, devices multi-select, dateFrom / dateTo, interval (auto / day / hour / minute), show empty sets.',
            'Manual "Show" trigger — no auto-refetch on filter change. Configure everything first, then run.',
            'KPIs at the top: Checked (count + percentage) and Total.',
            'Table of sums per ticket set with an expandable sub-table (per price level) and a grand total at the bottom.',
            'Chart: ApexCharts area chart of validations over time, one curve per device. Two-column layout.',
            'Deprecated checkpoints are filtered out client-side — they exist in the data but are not counted in the chart or sums.'
        ]
    },
    users: {
        title: 'Users',
        intro:
            'List of system users. Clicking a row opens an expand panel with three sections: Edit profile, Permissions matrix (per-instance), and Metadata. Permissions are saved per row (per IUR — see the Permissions section).',
        points: [
            'The backend was extended with admin.user.updateRights(userId, instanceId, …) — this lets you edit a user\'s permissions in a given instance, even if it is not their primary one.',
            'Username, password, and primary instance are hidden in the UI — we protect Bzuco sync so the integration is not accidentally broken.',
            'Inactive users appear dimmed (opacity-50), so you can immediately see who never signs in.',
            'Per-row dirty indicator: until you click "Save", permission changes stay local.'
        ]
    },
    permissions: {
        title: 'Permissions (IUR)',
        intro:
            'Permissions in Bzuco Inspector are per-instance (IUR = Instance User Role). A single user can have different permissions in each instance — typically admin in one, viewer in another.',
        points: [
            'Permission list: Manage users, Manage events, Manage ticket sets, Manage tickets, Manage devices, Statistics.',
            'Superusers implicitly have all permissions — hasRight() always returns true for them, regardless of IUR records.',
            'If someone cannot see an expected section in the menu, the first thing to check is their IUR in the Users section.',
            'Permission changes apply on the next request — the user does not have to sign out.'
        ]
    },
    import: {
        title: 'CSV ticket import',
        intro:
            'Bulk ticket import runs through the /tickets/import wizard. The wizard has 4 steps: file upload → preview with validation → field mapping → confirmation and submission to the backend.',
        points: [
            'Accepted format: CSV with 17 columns as a positional array. Czech headers (Číslo vstupenky, ID ceny, …) are tolerated.',
            'The preview shows the first 14 visible columns plus statistics on unique ticket sets and price levels in the file.',
            'Tickets are assigned to ticket sets by code prefix — the set must already exist in the system (synced from Bzuco POS) before importing.',
            'If the import succeeds but every field is NULL, the CSV format is wrong — see "What to do when".'
        ]
    },
    shortcuts: {
        title: 'Keyboard shortcuts',
        intro: 'The app intentionally adds very few shortcuts on top of the browser defaults so they do not get in the way.',
        points: [
            '"/" — open the filter bar (if present) and focus the search input. Works on Tickets, Statistics, and other filtered sections.',
            'Tab / Shift+Tab — standard form field navigation.',
            'Esc — close an open dialog or expand panel (where it makes sense).',
            'Standard browser shortcuts work normally (Ctrl/Cmd+F to search the rendered page, etc.).'
        ]
    },
    troubleshooting: {
        title: 'What to do when',
        intro: 'Common issues and how to solve them. If none of these help, see "Contact and bug reports".',
        points: [
            'I do not see a section in the menu — you are missing the permission. Ask an admin to update your IUR (Users → Permissions matrix).',
            'Tickets do not import / all fields are NULL — the CSV format is wrong. The backend reads a positional array, not a map. Verify the header (even in Czech) and that the exporter is not adding extra columns.',
            '"Updated X s ago" shows 0 or -1 — fixed in v0.7.0. If you still see -1, refresh the page (F5).',
            'After switching instance I still see old data — the composable did not pick up the invalidationKey. Refresh (F5) fixes it. Report if it keeps happening.',
            'Scanner offline alert even though the device is scanning — TicketUsageLog must arrive within < 1 h. If the scanner scans but validations are not saved, the issue is on the backend — contact Lukáš.'
        ]
    },
    contact: {
        title: 'Contact and bug reports',
        intro: 'Bzuco Inspector is an internal single-dev project. If you find a bug or have a suggestion, reach out directly.',
        points: [
            'Issue tracker: internal (bzuco_inspector_admin) — describe what happened, what you expected, and what the app showed.',
            'For critical bugs (validations fail, data loss, login broken) contact Lukáš directly.',
            'When reporting an issue, attach a screenshot and the URL — it speeds up diagnosis significantly.'
        ]
    }
}
